/**
 * Actions configuration section for the calling-rule modal.
 * Renders the list of action rows with type/field/value controls.
 */

import { SearchableSelect } from "@risalabs_frontend_org/oasis-ui-kit";
import { Button, Select, TextInput } from "risa-oasis-ui_v2";
import type {
  QueryBuilderConfig,
  QueryBuilderOption,
} from "../../../data-model/queryBuilder";
import type { CallingRuleAction } from "../../../pages/configurations/pages/callingConfiguration/callingRuleUtils";
import { AddIcon } from "../../../svg/add-icon";
import { Delete } from "../../../svg/delete";
import ActionDateValueInput from "../shared/ActionDateValueInput";
import ActionSetRandomValueInput from "../shared/ActionSetRandomValueInput";
import ActionSetValueInput from "../shared/ActionSetValueInput";
import {
  getActionFieldOptions,
  getActionTypeOptions,
  isActionFieldTimestamp,
  toValueString,
} from "../shared/ruleFormConstants";

interface CallingRuleActionsSectionProps {
  /** Condition query builder config (top_values on fields). */
  conditionConfig: QueryBuilderConfig;
  /** Same field options as the condition query builder's first column. */
  fieldOptions: QueryBuilderOption[];
  actions: CallingRuleAction[];
  onAddAction: () => void;
  onRemoveAction: (index: number) => void;
  onTypeChange: (index: number, value: string) => void;
  onFieldChange: (index: number, value: string) => void;
  onStringValueChange: (index: number, value: string) => void;
  onMultiValueChange: (index: number, values: string[]) => void;
  /** When false, hides the helper text under the Actions heading. */
  showHelperText?: boolean;
}

/** Renders the "Actions" card with add/remove rows for calling-rule actions. */
const CallingRuleActionsSection = ({
  conditionConfig,
  fieldOptions,
  actions,
  onAddAction,
  onRemoveAction,
  onTypeChange,
  onFieldChange,
  onStringValueChange,
  onMultiValueChange,
  showHelperText = true,
}: CallingRuleActionsSectionProps) => (
  <div className="flex flex-col gap-2 rounded-md border border-primaryGray-14 p-4">
    <div className="flex flex-row items-center justify-between">
      <div className="text-sm font-semibold">Actions</div>
      <Button
        buttonType="tertiary"
        size="small"
        disabled={false}
        onClick={onAddAction}
      >
        <AddIcon width="14" height="14" color="#0056d6" />
        Add action
      </Button>
    </div>
    {showHelperText && (
      <div className="text-tiny text-primaryGray-8">
        Each action sets a field to a value when the condition matches.
      </div>
    )}
    {actions.length === 0 ? (
      <div className="text-tiny text-primaryGray-12">
        No actions configured.
      </div>
    ) : (
      actions.map((action, index) => {
        const isSetRandom = action.type === "set_random";
        const isSet = action.type === "set";
        return (
          <div
            key={`action-${index}`}
            className="flex flex-col gap-2 rounded-md border border-primaryGray-16 p-3"
          >
            <div className="flex flex-row items-end gap-2">
              <div className="w-40">
                <Select
                  id={`action-type-${index}`}
                  label=""
                  options={getActionTypeOptions(action.type)}
                  placeholder="Select type"
                  defaultValue={action.type}
                  value={action.type}
                  onOptionChange={(d) => onTypeChange(index, d.value)}
                />
              </div>
              <div className="mb-1.5 min-w-0 flex-1">
                <SearchableSelect
                  id={`action-field-${index}`}
                  label=""
                  options={getActionFieldOptions(fieldOptions, action.field)}
                  value={action.field}
                  defaultValue={action.field}
                  placeholder="Select field..."
                  onOptionChange={(d) => onFieldChange(index, d.value)}
                />
              </div>
              {!isSetRandom && (
                <div className="mb-1.5 flex-1">
                  {isSet ? (
                    <ActionSetValueInput
                      id={`action-value-${index}`}
                      label=""
                      conditionConfig={conditionConfig}
                      selectedField={action.field}
                      value={action.value}
                      onValueChange={(v) => onStringValueChange(index, v)}
                      placeholder="Select value..."
                    />
                  ) : isActionFieldTimestamp(conditionConfig, action.field) ? (
                    <div className="-mt-1.5 w-full">
                      <ActionDateValueInput
                        id={`action-value-${index}`}
                        label=""
                        enabled={Boolean(action.field)}
                        value={action.value}
                        onValueChange={(v) => onStringValueChange(index, v)}
                      />
                    </div>
                  ) : (
                    <TextInput
                      id={`action-value-${index}`}
                      label=""
                      placeholder="e.g. yes"
                      defaultValue={toValueString(action.value)}
                      onChange={(d) =>
                        onStringValueChange(index, d.value ?? "")
                      }
                      onBlur={(d) => onStringValueChange(index, d.value ?? "")}
                    />
                  )}
                </div>
              )}
              <div
                className="mb-5 cursor-pointer"
                onClick={() => onRemoveAction(index)}
              >
                <Delete width={16} height={16} />
              </div>
            </div>
            {isSetRandom && (
              <ActionSetRandomValueInput
                id={`action-values-${index}`}
                label=""
                conditionConfig={conditionConfig}
                selectedField={action.field}
                value={action.value}
                onValuesChange={(vals) => onMultiValueChange(index, vals)}
              />
            )}
          </div>
        );
      })
    )}
  </div>
);

export default CallingRuleActionsSection;
